import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail as fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { IdCard, IndianRupee, Plus, Trash2, Lock, LogOut, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ExitDialog, RejoinDialog } from "@/components/hr/EmploymentDialogs";
import { toast } from "sonner";
import { initials } from "@/lib/ui";
import { inr } from "@/lib/hr";

const BASIC = [["employee_id", "Employee ID"], ["designation", "Designation"], ["department", "Department"], ["joining_date", "Date of joining", "date"], ["location", "Location"], ["phone", "Phone"]];
const SENSITIVE = [["bank_name", "Bank name"], ["bank_account", "Bank account no."], ["ifsc", "IFSC"], ["pan", "PAN"], ["uan", "UAN (12 digits)"]];

function ProfileDialog({ emp, salaryAdmin, onClose, onSaved }) {
  const [f, setF] = useState(() => Object.fromEntries([...BASIC, ...SENSITIVE].map(([k]) => [k, emp[k] || ""])));
  const fields = salaryAdmin ? [...BASIC, ...SENSITIVE] : BASIC;
  const save = async () => {
    const body = Object.fromEntries(fields.map(([k]) => [k, f[k]]));
    try { await api.put(`/hr/profiles/${emp.user.id}`, body); toast.success("Profile saved"); onSaved(); onClose(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
        <DialogHeader><DialogTitle className="font-display">{emp.user.name} — profile</DialogTitle><DialogDescription>{salaryAdmin ? "Includes bank and statutory details (visible only to you and the employee)." : "Bank and statutory details can only be edited by managers with HR rights."}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fields.map(([k, label, type]) => (
            <div key={k} className="space-y-1.5"><Label className="text-xs">{label}</Label><Input type={type || "text"} value={f[k]} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} data-testid={`profile-${k}-input`} /></div>
          ))}
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} className="bg-indigo-600 hover:bg-indigo-700" data-testid="profile-save-button">Save profile</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Lines({ title, rows, setRows, testid }) {
  const upd = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: k === "amount" ? v : v } : r)));
  return (
    <div className="space-y-2" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</p>
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2">
          <Input value={r.name} onChange={(e) => upd(i, "name", e.target.value)} className="h-9 flex-1" data-testid={`${testid}-name-${i}`} />
          <Input type="number" min="0" step="0.01" value={r.amount} onChange={(e) => upd(i, "amount", e.target.value)} className="h-9 w-32" data-testid={`${testid}-amount-${i}`} />
          <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-red-500" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => setRows([...rows, { name: "", amount: 0 }])} data-testid={`${testid}-add`}><Plus className="w-3.5 h-3.5 mr-1" />Add line</Button>
    </div>
  );
}

function SalaryDialog({ emp, onClose, onSaved }) {
  const [earn, setEarn] = useState(null);
  const [ded, setDed] = useState([]);
  useEffect(() => { api.get(`/hr/salary/${emp.user.id}`).then((r) => { setEarn(r.data.earnings); setDed(r.data.deductions); }); }, [emp]);
  const sum = (rows) => (rows || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const save = async () => {
    const clean = (rows) => rows.filter((r) => r.name.trim()).map((r) => ({ name: r.name.trim(), amount: Number(r.amount) || 0 }));
    try { await api.put(`/hr/salary/${emp.user.id}`, { earnings: clean(earn), deductions: clean(ded) }); toast.success("Salary structure saved"); onSaved(); onClose(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="salary-dialog">
        <DialogHeader><DialogTitle className="font-display">{emp.user.name} — monthly salary</DialogTitle><DialogDescription className="flex items-center gap-1"><Lock className="w-3 h-3" />Confidential. Visible only to managers with HR rights.</DialogDescription></DialogHeader>
        {earn ? (
          <div className="space-y-5">
            <Lines title="Earnings (monthly)" rows={earn} setRows={setEarn} testid="salary-earnings" />
            <Lines title="Deductions (monthly)" rows={ded} setRows={setDed} testid="salary-deductions" />
            <div className="rounded-lg bg-slate-50 p-3 grid grid-cols-3 text-center text-sm">
              <div><p className="text-[11px] text-slate-500">Gross</p><p className="font-semibold">{inr(sum(earn))}</p></div>
              <div><p className="text-[11px] text-slate-500">Deductions</p><p className="font-semibold">{inr(sum(ded))}</p></div>
              <div><p className="text-[11px] text-slate-500">Net</p><p className="font-display font-bold text-emerald-600" data-testid="salary-net-preview">{inr(sum(earn) - sum(ded))}</p></div>
            </div>
          </div>
        ) : <p className="text-sm text-slate-400">Loading…</p>}
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!earn} className="bg-indigo-600 hover:bg-indigo-700" data-testid="salary-save-button">Save salary</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EmployeesTab({ salaryAdmin }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [salary, setSalary] = useState(null);
  const [exiting, setExiting] = useState(null);
  const [rejoining, setRejoining] = useState(null);
  const { user: me } = useAuth();
  const canChange = (e) => salaryAdmin && e.user.id !== me?.id && !e.user.is_owner && (e.user.role === "engineer" || me?.is_owner);
  const sorted = [...list].sort((a, b) => Boolean(a.exited_on) - Boolean(b.exited_on));
  const load = useCallback(() => api.get("/hr/profiles").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="employees-tab">
      {sorted.map((e) => (
        <div key={e.user.id} className={`border rounded-xl p-4 flex items-center gap-3 flex-wrap ${e.exited_on ? "bg-slate-50 border-slate-200 opacity-80" : "bg-white border-slate-200"}`} data-testid={`employee-card-${e.user.id}`}>
          <Avatar className="w-10 h-10"><AvatarImage src={e.user.avatar} /><AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs">{initials(e.user.name)}</AvatarFallback></Avatar>
          <div className="flex-1 min-w-[140px]">
            <p className="text-sm font-semibold text-slate-900">{e.user.name} {e.employee_id && <span className="font-mono text-[11px] text-slate-400 ml-1">{e.employee_id}</span>}</p>
            <p className="text-xs text-slate-500">{[e.designation, e.department].filter(Boolean).join(" · ") || "Profile not set"}</p>
            {e.exited_on && <span className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wider text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5" data-testid={`employee-exited-badge-${e.user.id}`}>Exited · last day {e.exited_on}</span>}
            {!e.exited_on && e.rejoined_on && <span className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`employee-rejoined-badge-${e.user.id}`}>Rejoined {e.rejoined_on}</span>}
          </div>
          <Button size="sm" variant="outline" onClick={() => setEditing(e)} data-testid={`employee-profile-${e.user.id}`}><IdCard className="w-3.5 h-3.5 mr-1" />Profile</Button>
          {salaryAdmin && <Button size="sm" variant={e.has_salary ? "outline" : "default"} className={e.has_salary ? "" : "bg-indigo-600 hover:bg-indigo-700"} onClick={() => setSalary(e)} data-testid={`employee-salary-${e.user.id}`}><IndianRupee className="w-3.5 h-3.5 mr-1" />{e.has_salary ? "Salary" : "Set salary"}</Button>}
          {canChange(e) && (e.exited_on
            ? <Button size="sm" variant="outline" onClick={() => setRejoining(e)} data-testid={`employee-rejoin-${e.user.id}`}><RotateCcw className="w-3.5 h-3.5 mr-1" />Rejoin</Button>
            : <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setExiting(e)} data-testid={`employee-exit-${e.user.id}`}><LogOut className="w-3.5 h-3.5 mr-1" />Mark as exited</Button>)}
        </div>
      ))}
      {editing && <ProfileDialog emp={editing} salaryAdmin={salaryAdmin} onClose={() => setEditing(null)} onSaved={load} />}
      {salary && <SalaryDialog emp={salary} onClose={() => setSalary(null)} onSaved={load} />}
      {exiting && <ExitDialog emp={exiting} onClose={() => setExiting(null)} onSaved={load} />}
      {rejoining && <RejoinDialog emp={rejoining} onClose={() => setRejoining(null)} onSaved={load} />}
    </div>
  );
}
